import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { EditorContent, useEditor } from '@tiptap/react'

import { Alert } from '@/components/feedback/Alert'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LiveDocumentSource } from '@/features/documents/nodes/live-source'
import { DOCUMENT_CLASS } from '@/features/documents/nodes/document-class'
import { useReportFilters } from '@/features/report/use-report-filters'
import { useCubeMeta } from '@/hooks/use-cube-meta'
import { useLeaveGuard } from '@/lib/form/use-leave-guard'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { reportDocumentSchema } from '@/lib/report-document'
import { cn } from '@/lib/utils'
import { isVersionConflict, useBiReport, useSaveReport, type BiReport } from '@/services/bi/reports'
import { documentFilterDefs } from '@/services/cube/document'

import { BlockSettings } from './BlockSettings'
import { EditorToolbar } from './EditorToolbar'
import { createDocumentExtensions } from './extensions'
import { PreviewFilters } from './PreviewFilters'
import { countQueryUsage } from './refs'
import {
  buildDefinition,
  checkDefinition,
  getDocumentJSON,
  type DocumentTemplate,
} from './serialize'
import { TemplateSettings } from './TemplateSettings'
import { VersionsPanel } from './VersionsPanel'

const t = messages.documents.editor

function ErrorList({ title, errors }: { title: string; errors: string[] }) {
  return (
    <Alert tone="danger" title={title}>
      <ul className="list-disc pl-5">
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </Alert>
  )
}

/** Kayıtlı tanım şemaya uymuyorsa editör açılmaz; hata yolları listelenir. */
function InvalidDefinition({ definition }: { definition: unknown }) {
  const check = checkDefinition(definition)
  return <ErrorList title={t.invalidDefinition} errors={check.ok ? [] : check.errors} />
}

export interface TemplateEditorProps {
  report: BiReport
  /** Sunucudaki en güncel kopyayı yeniden çekip editörü baştan kurar (çakışma sonrası). */
  onReload: () => void
  /** Önbellekteki kopyayla editörü baştan kurar (eski sürüme dönüldükten sonra). */
  onRemount: () => void
}

function TemplateEditor({ report, onReload, onRemount }: TemplateEditorProps) {
  const canEdit = report.can_edit
  const meta = useCubeMeta().data
  const save = useSaveReport(report.id)

  // Şablon düzeyi alanlar React durumunda; içerik TipTap'te. Başlangıç tanımı bir kez ayrıştırılır.
  const [template, setTemplate] = useState<DocumentTemplate>(() => {
    const { title, orientation, filters, queries } = reportDocumentSchema.parse(report.definition)
    return { title, orientation, filters, queries }
  })
  const [visibility, setVisibility] = useState(report.visibility)
  const [, setRevision] = useState(0)
  const [errors, setErrors] = useState<string[]>()
  const [conflict, setConflict] = useState(false)
  const [savedVersion, setSavedVersion] = useState<number>()

  const editor = useEditor({
    extensions: createDocumentExtensions(),
    // Ham içerik (şema varsayılanları eklenmemiş): editör JSON'u kayıtlı JSON ile aynı kalır.
    content: (report.definition as { content: object }).content,
    editable: canEdit,
    onUpdate: () => setRevision((revision) => revision + 1),
  })

  const content = getDocumentJSON(editor)
  const snapshot = JSON.stringify({ template, visibility, content })
  const [saved, setSaved] = useState(snapshot)
  const dirty = canEdit && snapshot !== saved
  const leaveGuard = useLeaveGuard(dirty)

  const reportFilters = useReportFilters(
    useMemo(() => ({ filters: documentFilterDefs(template.filters) }), [template.filters]),
  )

  const onSave = () => {
    const definition = buildDefinition(template, content)
    const check = checkDefinition(definition)
    setSavedVersion(undefined)
    if (!check.ok) return setErrors(check.errors)
    setErrors(undefined)
    setConflict(false)
    save.mutate(
      {
        definition,
        expected_version: report.version,
        ...(visibility !== report.visibility && { visibility }),
      },
      {
        onSuccess: (updated) => {
          setSaved(snapshot)
          setSavedVersion(updated.version)
        },
        onError: (error) => setConflict(isVersionConflict(error)),
      },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {leaveGuard.dialog}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 w-fit"
            render={<Link to="/belgeler" />}
            nativeButton={false}
          >
            <ArrowLeft />
            {t.back}
          </Button>
          <h1 className="text-lg font-semibold">{template.title || t.untitled}</h1>
        </div>
        {canEdit && (
          <div className="flex items-center gap-3">
            {dirty && <span className="text-muted-foreground text-sm">{t.unsaved}</span>}
            {!dirty && savedVersion !== undefined && (
              <span role="status" className="text-positive text-sm">
                {t.saved(savedVersion)}
              </span>
            )}
            <Button onClick={onSave} disabled={save.isPending || !dirty}>
              <Save />
              {save.isPending ? t.saving : t.save}
            </Button>
          </div>
        )}
      </div>

      {!canEdit && <p className="text-muted-foreground text-sm">{t.readOnly}</p>}
      {errors && <ErrorList title={t.invalidTitle} errors={errors} />}
      {conflict && (
        <Alert
          tone="danger"
          title={t.conflict}
          action={
            <Button size="sm" variant="outline" onClick={onReload}>
              {t.reload}
            </Button>
          }
        />
      )}
      {save.isError && !conflict && <ErrorState error={save.error} />}

      <PreviewFilters filters={template.filters} reportFilters={reportFilters} />
      {canEdit && <EditorToolbar editor={editor} meta={meta} template={template} />}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <LiveDocumentSource
            meta={meta}
            filters={template.filters}
            queries={template.queries}
            reportFilters={reportFilters}
          >
            <div
              aria-label={t.pageLabel}
              className={cn(
                'border-border bg-card text-card-foreground mx-auto min-h-96 w-full rounded-md border p-8',
                template.orientation === 'landscape' ? 'max-w-[297mm]' : 'max-w-[210mm]',
              )}
            >
              <EditorContent editor={editor} className={DOCUMENT_CLASS} />
            </div>
          </LiveDocumentSource>
        </div>

        {canEdit && (
          <aside className="border-border bg-card w-full shrink-0 rounded-md border p-3 lg:sticky lg:top-0 lg:w-80">
            <Tabs defaultValue="block">
              <TabsList>
                <TabsTrigger value="block">{t.tabs.block}</TabsTrigger>
                <TabsTrigger value="template">{t.tabs.template}</TabsTrigger>
                <TabsTrigger value="versions">{t.tabs.versions}</TabsTrigger>
              </TabsList>
              <TabsContent value="block" className="pt-2">
                <BlockSettings editor={editor} meta={meta} template={template} />
              </TabsContent>
              <TabsContent value="template" className="pt-2">
                <TemplateSettings
                  meta={meta}
                  template={template}
                  onChange={setTemplate}
                  usage={(name) => countQueryUsage(content, name)}
                  visibility={visibility}
                  onVisibilityChange={setVisibility}
                />
              </TabsContent>
              <TabsContent value="versions" className="pt-2">
                <VersionsPanel
                  reportId={report.id}
                  canEdit={canEdit}
                  currentVersion={report.version}
                  dirty={dirty}
                  onRestored={onRemount}
                />
              </TabsContent>
            </Tabs>
          </aside>
        )}
      </div>
    </div>
  )
}

/** Belge şablonu editörü (`/belgeler/:id/duzenle`). */
export function TemplateEditorPage() {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const report = useBiReport(id)
  // Editörü baştan kurmak için anahtar: "yeniden yükle" ve "sürüme dön" sonrası.
  const [reloadKey, setReloadKey] = useState(0)

  if (report.isLoading) return <Loading rows={8} />
  if (report.isError || !report.data) {
    return <ErrorState error={report.error} onRetry={() => void report.refetch()} />
  }
  const data = report.data
  if (!reportDocumentSchema.safeParse(data.definition).success) {
    return <InvalidDefinition definition={data.definition} />
  }
  return (
    <TemplateEditor
      key={`${data.id}:${reloadKey}`}
      report={data}
      onRemount={() => setReloadKey((key) => key + 1)}
      onReload={() => {
        void queryClient
          .invalidateQueries({ queryKey: queryKeys.bi.report(data.id), exact: true })
          .then(() => setReloadKey((key) => key + 1))
      }}
    />
  )
}
